// DEPENDENCIES
import React, { useContext, useEffect, useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { postAPICall, putAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import {
	Alert,
	Autocomplete,
	Button,
	Checkbox,
	CircularProgress,
	Collapse,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	FormHelperText,
	IconButton,
	MenuItem,
	TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const ADMIN_PROJECT_ID = "afa51d1e-bcd8-4acc-9d45-cb6bbb590c44"; // J2 Consulting Overhead | Admin

const blankForm = () => ({
	bambooType: null,
	bqe_activity_id: "",
	bqe_project_id: ADMIN_PROJECT_ID,
	post_hours: true,
	is_synced: true,
});

// REACT COMPONENT
// `type` is {} to add a new type, or a time_off_types row to edit
const EditTimeOffTypeDialog = ({
	open,
	type,
	activities,
	projects,
	bambooTypes,
	onClose,
	onSaved,
}) => {
	const { instance, accounts } = useMsal();
	const employeeID = useContext(UserContext);

	const isEdit = Boolean(type?.id);
	const [form, setForm] = useState(blankForm);
	const [errorText, setErrorText] = useState("");
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (!open) return;
		setErrorText("");
		setForm(
			isEdit
				? {
						bambooType: {
							id: type.bamboohr_type_id,
							name: type.name,
						},
						bqe_activity_id: type.bqe_activity_id || "",
						bqe_project_id: type.bqe_project_id || "",
						post_hours: type.post_hours,
						is_synced: type.is_synced,
					}
				: blankForm(),
		);
	}, [open, type, isEdit]);

	const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

	const handleSave = async () => {
		if (!form.bambooType) {
			setErrorText("Please choose a BambooHR time off type.");
			return;
		}
		if (form.is_synced && (!form.bqe_activity_id || !form.bqe_project_id)) {
			setErrorText(
				"Please choose a BQE activity and project, or turn sync off.",
			);
			return;
		}

		const body = {
			bamboohr_type_id: form.bambooType.id,
			name: form.bambooType.name,
			bqe_activity_id: form.bqe_activity_id || null,
			bqe_project_id: form.bqe_project_id || null,
			post_hours: form.post_hours,
			is_synced: form.is_synced,
		};

		setIsSaving(true);
		try {
			if (isEdit) {
				await putAPICall(
					instance,
					accounts[0],
					`/api/time-off/types/${type.id}`,
					body,
					employeeID,
				);
			} else {
				await postAPICall(
					instance,
					accounts[0],
					"/api/time-off/types",
					{ ...body, modifiedBy: employeeID },
				);
			}
			onSaved();
		} catch (err) {
			console.error("Error saving time off type:", err);
			setErrorText(
				err?.response?.data?.error ||
					"Couldn't save the time off type.",
			);
		}
		setIsSaving(false);
	};

	const selectedActivity =
		activities.find((a) => a.id === form.bqe_activity_id) || null;

	return (
		<Dialog
			id="EditTimeOffTypeDialog"
			open={open}
			fullWidth
			maxWidth="sm"
			onClose={onClose}
		>
			<DialogTitle id="EditTimeOffTypeDialogTitle">
				{isEdit ? "Edit Time Off Type" : "New Time Off Type"}
			</DialogTitle>
			<DialogContent id="EditTimeOffTypeDialogContent">
				<Collapse in={Boolean(errorText)}>
					<Alert
						severity="error"
						action={
							<IconButton
								aria-label="close"
								color="inherit"
								size="small"
								onClick={() => setErrorText("")}
							>
								<CloseIcon fontSize="inherit" />
							</IconButton>
						}
					>
						{errorText}
					</Alert>
				</Collapse>

				{isEdit ? (
					<TextField
						label="BambooHR Type"
						value={`${form.bambooType?.name || ""} (#${form.bambooType?.id || ""})`}
						disabled
						helperText="Set by BambooHR."
					/>
				) : (
					<Autocomplete
						options={bambooTypes}
						getOptionLabel={(t) => `${t.name} (#${t.id})`}
						isOptionEqualToValue={(a, b) => a.id === b.id}
						value={form.bambooType}
						onChange={(_, v) => set("bambooType", v)}
						noOptionsText="Every BambooHR type is already mapped"
						renderInput={(params) => (
							<TextField {...params} label="BambooHR Type" />
						)}
					/>
				)}

				<Autocomplete
					options={activities}
					getOptionLabel={(a) => a.name}
					isOptionEqualToValue={(a, b) => a.id === b.id}
					value={selectedActivity}
					onChange={(_, v) => set("bqe_activity_id", v?.id || "")}
					renderInput={(params) => (
						<TextField
							{...params}
							label="BQE Activity"
							helperText="Days of this type are posted to BQE under this activity."
						/>
					)}
				/>

				<TextField
					select
					label="BQE Project"
					value={form.bqe_project_id}
					onChange={(e) => set("bqe_project_id", e.target.value)}
				>
					<MenuItem value="">None</MenuItem>
					{projects.map((p) => (
						<MenuItem key={p.id} value={p.id}>
							{p.name}
						</MenuItem>
					))}
				</TextField>

				<div>
					<FormControlLabel
						control={
							<Checkbox
								checked={form.is_synced}
								onChange={(e) =>
									set("is_synced", e.target.checked)
								}
							/>
						}
						label="Sync this type to BQE"
					/>
					<FormHelperText className="timeOffConsoleCheckboxHelp">
						When off, requests of this type are recorded but nothing
						is posted to BQE (e.g. parental leave).
					</FormHelperText>
				</div>
				<div>
					<FormControlLabel
						control={
							<Checkbox
								checked={form.post_hours}
								onChange={(e) =>
									set("post_hours", e.target.checked)
								}
							/>
						}
						label="Post hours"
					/>
					<FormHelperText className="timeOffConsoleCheckboxHelp">
						When off, days of this type are added to BQE with 0
						hours (e.g. unpaid time off).
					</FormHelperText>
				</div>

				<div className="dialogButtons">
					<Button
						onClick={onClose}
						variant="outlined"
						color="error"
						disabled={isSaving}
					>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						variant="contained"
						disableElevation
						disabled={isSaving}
						startIcon={
							isSaving ? (
								<CircularProgress size={16} color="inherit" />
							) : null
						}
					>
						Save
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default EditTimeOffTypeDialog;
